import { DeleteOutlined, EyeOutlined, PlusOutlined, PrinterOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Descriptions, Form, Input, InputNumber, Modal, Row, Select, Space, Table, Tag, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from "react-toastify";
import { responseStatus, urlAdmin } from "../../constants";
import { Links } from '../../helpers/Links';
import { dateCommon, formatVND, validateMessages } from "../../helpers/common";
import { TaiSan, TaiSanRequest, TrangThaiTaiSan, DMCoSo, DMBopPhanKiemKe } from "../../models/taisan/TaiSan";
import { DataSelectBox } from "../../models/selectbox";
import { layDsLoaiTaiSan } from "../../services/danhmuc";
import { layDsCoSo, layDsBoPhanKiemKe, luuTaiSan, xoaTaiSanPhu } from "../../services/taisan";
import DonViSelectTree from '../utils/DonViSelectTree';
import NhanVienSelectBox from '../utils/NhanVienSelectBox';

const { TextArea } = Input;

interface TaiSanPhuPending {
    tempId: number;
    ma: string;
    maKeToan?: string;
    tenTaiSan: string;
    idLoaiTaiSan?: number;
    tenLoaiTaiSan?: string;
    idCoSo?: number;
    tenCoSo?: string;
    idBoPhan?: number;
    tenBoPhan?: string;
    idNVSuDung?: number;
    nguoiSuDung?: string;
    soLuong: number;
    viTriSuDung?: string;
    trangThai: number;
    ngayTang?: string;
    ngayNhapKho?: string;
    ngayDuaVaoSuDung?: string;
    soKyPhanBo?: number;
    soNamPhanBo?: number | null;
    nguyenGia?: number;
    giaTriKhiNhap?: number;
    giaTriDaPhanBo?: number;
    giaTriConLai?: number;
    soSerial?: string;
    nhaCungCap?: string;
    thoiGianBaoHanh?: number;
    moTa?: string;
}

const LuuTaiSanBox = ({ modelProps, onSubmit }: {
    modelProps: TaiSan | null,
    onSubmit: () => void
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [formTaiSanPhu] = Form.useForm();

    const [optionLoaiTaiSan, setOptionLoaiTaiSan] = useState<Array<DataSelectBox>>([]);
    const [optionCoSo, setOptionCoSo] = useState<Array<DataSelectBox>>([]);
    const [optionBoPhan, setOptionBoPhan] = useState<Array<DataSelectBox>>([]);
    const [idsTaiLieuTaiSanXoa, setIdsTaiLieuTaiSanXoa] = useState<Array<number>>([]);

    const [showModalTaiSanPhu, setShowModalTaiSanPhu] = useState<boolean>(false);
    const [dsTaiSanPhuPending, setDsTaiSanPhuPending] = useState<TaiSanPhuPending[]>([]);
    const [dsTaiSanPhuHienTai, setDsTaiSanPhuHienTai] = useState<TaiSan[]>([]);
    const [idsXoaTaiSanPhu, setIdsXoaTaiSanPhu] = useState<number[]>([]);
    const [editingTaiSanPhu, setEditingTaiSanPhu] = useState<TaiSanPhuPending | null>(null);
    const [tempIdCounter, setTempIdCounter] = useState<number>(-1);

    // Modal xem chi tiết tài sản phụ hiện tại
    const [showModalXemPhuHienTai, setShowModalXemPhuHienTai] = useState<boolean>(false);
    const [taiSanPhuDangXem, setTaiSanPhuDangXem] = useState<TaiSan | null>(null);
    const componentRefPhuXem = useRef<any>();

    const handlePrintPhuXem = useReactToPrint({
        content: () => componentRefPhuXem.current,
        documentTitle: `Tem_QR_phu_${taiSanPhuDangXem?.ma}_${new Date().getTime()}`,
    });

    // Modal sửa tài sản phụ hiện tại (đã có trong DB)
    const [showModalSuaPhuHienTai, setShowModalSuaPhuHienTai] = useState<boolean>(false);
    const [suaPhuHienTaiId, setSuaPhuHienTaiId] = useState<number | null>(null);
    const [formSuaPhuHienTai] = Form.useForm();

    const fileHienTai: any = (modelProps?.dsTaiLieu || []).map((item: any) => ({
        uid: item.idTaiLieu + "tailieu",
        name: item.tenTep,
        status: 'done',
        url: urlAdmin + item.duongDan.replace(/\\/g, "/"),
    }));

    const [fileList, setFileList] = useState<UploadFile[]>(fileHienTai);

    const trangThaiOptions = [
        { value: TrangThaiTaiSan.MoiNhap, label: 'Mới nhập' },
        { value: TrangThaiTaiSan.DangSuDung, label: 'Đang sử dụng' },
        { value: TrangThaiTaiSan.BaoTri, label: 'Bảo trì' },
        { value: TrangThaiTaiSan.Hong, label: 'Hỏng' },
        { value: TrangThaiTaiSan.ChoThanhLy, label: 'Chờ thanh lý' },
        { value: TrangThaiTaiSan.DieuChuyen, label: 'Điều chuyển' },
        { value: TrangThaiTaiSan.DaThanhLy, label: 'Đã thanh lý' },
    ];

    const getTrangThaiTag = (trangThai: number) => {
        switch (trangThai) {
            case TrangThaiTaiSan.MoiNhap: return <Tag color="blue">Mới nhập</Tag>;
            case TrangThaiTaiSan.DangSuDung: return <Tag color="green">Đang sử dụng</Tag>;
            case TrangThaiTaiSan.BaoTri: return <Tag color="orange">Bảo trì</Tag>;
            case TrangThaiTaiSan.Hong: return <Tag color="red">Hỏng</Tag>;
            case TrangThaiTaiSan.ChoThanhLy: return <Tag color="purple">Chờ thanh lý</Tag>;
            case TrangThaiTaiSan.DieuChuyen: return <Tag color="cyan">Điều chuyển</Tag>;
            case TrangThaiTaiSan.DaThanhLy: return <Tag color="red">Đã thanh lý</Tag>;
            default: return <Tag>Không xác định</Tag>;
        }
    };

    const setFormDetailModal = (value: TaiSan | null) => {
        form.setFieldsValue({
            id: value?.id,
            ma: value?.ma,
            maKeToan: value?.maKeToan,
            tenTaiSan: value?.tenTaiSan,
            idLoaiTaiSan: value?.idLoaiTaiSan,
            idCoSo: value?.idCoSo,
            idBoPhan: value?.idBoPhan,
            idDonVi: value?.idDonVi,
            idNVSuDung: value?.idNVSuDung,
            viTriSuDung: value?.viTriSuDung,
            ngayTang: value?.ngayTang ? dayjs(dateCommon.formatDate(value.ngayTang), "DD/MM/YYYY") : null,
            ngayDuaVaoSuDung: value?.ngayDuaVaoSuDung ? dayjs(dateCommon.formatDate(value.ngayDuaVaoSuDung), "DD/MM/YYYY") : null,
            ngayNhapKho: value?.ngayNhapKho ? dayjs(dateCommon.formatDate(value.ngayNhapKho), "DD/MM/YYYY") : null,
            soNamPhanBo: value?.soNamPhanBo,
            soKyPhanBo: value?.soKyPhanBo,
            soKyDaPhanBo: value?.soKyDaPhanBo,
            giaTriKhiNhap: value?.giaTriKhiNhap,
            nguyenGia: value?.nguyenGia ?? value?.giaTriKhiNhap,
            giaTriDaPhanBo: value?.giaTriDaPhanBo,
            giaTriConLai: value?.giaTriConLai,
            trangThai: value?.trangThai ?? TrangThaiTaiSan.MoiNhap,
            soSerial: value?.soSerial,
            nhaCungCap: value?.nhaCungCap,
            soLuong: value?.soLuong || 1,
            thoiGianBaoHanh: value?.thoiGianBaoHanh,
            moTa: value?.moTa,
        });
        if (value?.dsTaiSanPhu && value.dsTaiSanPhu.length > 0) {
            setDsTaiSanPhuHienTai(value.dsTaiSanPhu as TaiSan[]);
        } else {
            setDsTaiSanPhuHienTai([]);
        }
        setDsTaiSanPhuPending([]);
        setIdsXoaTaiSanPhu([]);
    };

    const uploadProps: UploadProps = {
        onRemove: (file) => {
            if (file.uid.includes("tailieu")) {
                const id = parseInt(file.uid.split("tailieu")[0]);
                setIdsTaiLieuTaiSanXoa(prev => [...prev, id]);
            }
            setFileList(prev => {
                const index = prev.indexOf(file);
                const next = prev.slice();
                next.splice(index, 1);
                return next;
            });
        },
        beforeUpload: (file) => {
            const maxSize = 20000000;
            if (file.size > maxSize) {
                alert('Tệp tin đính kèm không được vượt quá 20MB');
            } else {
                setFileList(prev => [...prev, file]);
            }
            return false;
        },
        fileList,
    };

    const loadDataSelectBoxs = async () => {
        try {
            setIsLoading(true);
            const [loaiTaiSan, dsCoSo, dsBoPhan] = await Promise.all([
                layDsLoaiTaiSan(),
                layDsCoSo(),
                layDsBoPhanKiemKe()
            ]);
            setOptionLoaiTaiSan(loaiTaiSan);
            setOptionCoSo(dsCoSo.map(x => ({ value: x.id, label: x.tenCoSo })));
            setOptionBoPhan(dsBoPhan.map(x => ({ 
                value: x.id, 
                label: `${x.nhomCap1 ? `[${x.nhomCap1}] ` : ''}${x.ten}` 
            })));
        } catch (err: any) {
            toast.error('Lỗi khi tải dữ liệu: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMoModalTaiSanPhu = (record?: TaiSanPhuPending) => {
        formTaiSanPhu.resetFields();
        if (record) {
            setEditingTaiSanPhu(record);
            formTaiSanPhu.setFieldsValue({
                ...record,
                nguyenGia: record.nguyenGia ?? record.giaTriKhiNhap,
                ngayTang: record.ngayTang ? dayjs(record.ngayTang, "DD/MM/YYYY") : null,
                ngayNhapKho: record.ngayNhapKho ? dayjs(record.ngayNhapKho, "DD/MM/YYYY") : null,
                ngayDuaVaoSuDung: record.ngayDuaVaoSuDung ? dayjs(record.ngayDuaVaoSuDung, "DD/MM/YYYY") : null,
            });
        } else {
            setEditingTaiSanPhu(null);
            formTaiSanPhu.setFieldsValue({
                trangThai: TrangThaiTaiSan.MoiNhap,
                soLuong: 1,
                idLoaiTaiSan: form.getFieldValue('idLoaiTaiSan'),
                idCoSo: form.getFieldValue('idCoSo'),
                idBoPhan: form.getFieldValue('idBoPhan'),
                idNVSuDung: form.getFieldValue('idNVSuDung'),
                viTriSuDung: form.getFieldValue('viTriSuDung'),
                ngayTang: form.getFieldValue('ngayTang'),
                ngayNhapKho: form.getFieldValue('ngayNhapKho'),
                ngayDuaVaoSuDung: form.getFieldValue('ngayDuaVaoSuDung'),
            });
        }
        setShowModalTaiSanPhu(true);
    };

    const handleLuuTaiSanPhu = () => {
        formTaiSanPhu.validateFields().then((values) => {
            const tenLoaiTaiSan = optionLoaiTaiSan.find(x => x.value === values.idLoaiTaiSan)?.label as string;
            const tenCoSo = optionCoSo.find(x => x.value === values.idCoSo)?.label as string;
            const tenBoPhan = optionBoPhan.find(x => x.value === values.idBoPhan)?.label as string;
            const ngayTang = values.ngayTang ? dateCommon.formatDate(values.ngayTang?.$d) : undefined;
            const ngayNhapKho = values.ngayNhapKho ? dateCommon.formatDate(values.ngayNhapKho?.$d) : undefined;
            const ngayDuaVaoSuDung = values.ngayDuaVaoSuDung ? dateCommon.formatDate(values.ngayDuaVaoSuDung?.$d) : undefined;
            const giaTriConLai = values.giaTriConLai ?? ((values.nguyenGia ?? values.giaTriKhiNhap ?? 0) - (values.giaTriDaPhanBo ?? 0));
            const phuData = {
                ...values,
                tenLoaiTaiSan,
                tenCoSo,
                tenBoPhan,
                ngayTang,
                ngayNhapKho,
                ngayDuaVaoSuDung,
                giaTriKhiNhap: values.nguyenGia ?? values.giaTriKhiNhap,
                nguyenGia: values.nguyenGia ?? values.giaTriKhiNhap,
                giaTriConLai,
            };
            if (editingTaiSanPhu) {
                setDsTaiSanPhuPending(prev => prev.map(item =>
                    item.tempId === editingTaiSanPhu.tempId
                        ? { ...item, ...phuData }
                        : item
                ));
            } else {
                const newRecord: TaiSanPhuPending = {
                    tempId: tempIdCounter,
                    ...phuData,
                };
                setTempIdCounter(prev => prev - 1);
                setDsTaiSanPhuPending(prev => [...prev, newRecord]);
            }
            setShowModalTaiSanPhu(false);
        });
    };

    const handleXoaTaiSanPhuPending = (tempId: number) => {
        setDsTaiSanPhuPending(prev => prev.filter(x => x.tempId !== tempId));
    };

    const handleXoaTaiSanPhuHienTai = (id: number) => {
        setDsTaiSanPhuHienTai(prev => prev.filter(x => x.id !== id));
        setIdsXoaTaiSanPhu(prev => [...prev, id]);
    };

    const handleXemPhuHienTai = (record: TaiSan) => {
        setTaiSanPhuDangXem(record);
        setShowModalXemPhuHienTai(true);
    };

    const handleMoModalSuaPhuHienTai = (record: TaiSan) => {
        setSuaPhuHienTaiId(record.id!);
        formSuaPhuHienTai.resetFields();
        formSuaPhuHienTai.setFieldsValue({
            ...record,
            nguyenGia: record.nguyenGia ?? record.giaTriKhiNhap,
            ngayTang: record.ngayTang ? dayjs(dateCommon.formatDate(record.ngayTang), 'DD/MM/YYYY') : null,
            ngayNhapKho: record.ngayNhapKho ? dayjs(dateCommon.formatDate(record.ngayNhapKho), 'DD/MM/YYYY') : null,
            ngayDuaVaoSuDung: record.ngayDuaVaoSuDung ? dayjs(dateCommon.formatDate(record.ngayDuaVaoSuDung), 'DD/MM/YYYY') : null,
        });
        setShowModalSuaPhuHienTai(true);
    };

    const handleLuuSuaPhuHienTai = () => {
        formSuaPhuHienTai.validateFields().then(async (values) => {
            try {
                const ngayTang = values.ngayTang ? dateCommon.formatDate(values.ngayTang?.$d) : undefined;
                const ngayNhapKho = values.ngayNhapKho ? dateCommon.formatDate(values.ngayNhapKho?.$d) : undefined;
                const ngayDuaVaoSuDung = values.ngayDuaVaoSuDung ? dateCommon.formatDate(values.ngayDuaVaoSuDung?.$d) : undefined;
                const giaTriConLai = values.giaTriConLai ?? ((values.nguyenGia ?? values.giaTriKhiNhap ?? 0) - (values.giaTriDaPhanBo ?? 0));
                const req: TaiSanRequest = {
                    files: [],
                    id: suaPhuHienTaiId!,
                    ma: values.ma,
                    maKeToan: values.maKeToan,
                    tenTaiSan: values.tenTaiSan,
                    idLoaiTaiSan: values.idLoaiTaiSan,
                    idCoSo: values.idCoSo,
                    idBoPhan: values.idBoPhan,
                    idDonVi: form.getFieldValue('idDonVi'),
                    idNVSuDung: values.idNVSuDung,
                    viTriSuDung: values.viTriSuDung,
                    ngayTang,
                    ngayNhapKho,
                    ngayDuaVaoSuDung,
                    soNamPhanBo: values.soNamPhanBo,
                    soKyPhanBo: values.soKyPhanBo,
                    soKyDaPhanBo: values.soKyDaPhanBo,
                    giaTriKhiNhap: values.nguyenGia ?? values.giaTriKhiNhap,
                    nguyenGia: values.nguyenGia ?? values.giaTriKhiNhap,
                    giaTriDaPhanBo: values.giaTriDaPhanBo,
                    giaTriConLai,
                    trangThai: values.trangThai,
                    soSerial: values.soSerial,
                    nhaCungCap: values.nhaCungCap,
                    soLuong: values.soLuong,
                    thoiGianBaoHanh: values.thoiGianBaoHanh,
                    moTa: values.moTa,
                    idTaiSanChinh: modelProps?.id,
                };
                await luuTaiSan(req);
                toast.success('Cập nhật tài sản phụ thành công');
                setShowModalSuaPhuHienTai(false);
                const tenLoaiTaiSan = optionLoaiTaiSan.find(x => x.value === values.idLoaiTaiSan)?.label as string;
                const tenCoSo = optionCoSo.find(x => x.value === values.idCoSo)?.label as string;
                const tenBoPhan = optionBoPhan.find(x => x.value === values.idBoPhan)?.label as string;

                setDsTaiSanPhuHienTai(prev => prev.map(x =>
                    x.id === suaPhuHienTaiId ? {
                        ...x,
                        ...values,
                        loaiTaiSan: tenLoaiTaiSan ?? x.loaiTaiSan,
                        tenCoSo: tenCoSo ?? x.tenCoSo,
                        tenBoPhan: tenBoPhan ?? x.tenBoPhan,
                        ngayTang,
                        ngayNhapKho,
                        ngayDuaVaoSuDung,
                        nguyenGia: values.nguyenGia ?? values.giaTriKhiNhap,
                        giaTriDaPhanBo: values.giaTriDaPhanBo,
                        giaTriConLai
                    } : x
                ));
            } catch (error: any) {
                toast.error('Lỗi khi cập nhật tài sản phụ: ' + error.message);
            }
        });
    };

    const renderChiTietTaiSanPhu = (ts: any) => {
        return (
            <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Tên tài sản">{ts.tenTaiSan}</Descriptions.Item>
                <Descriptions.Item label="Mã tài sản (HRM)">{ts.ma}</Descriptions.Item>
                <Descriptions.Item label="Mã kế toán (FAST)">{ts.maKeToan || '-'}</Descriptions.Item>
                <Descriptions.Item label="Loại tài sản">{ts.loaiTaiSan || ts.tenLoaiTaiSan || '-'}</Descriptions.Item>
                <Descriptions.Item label="Cơ sở">{ts.tenCoSo || '-'}</Descriptions.Item>
                <Descriptions.Item label="Vị trí">{ts.tenBoPhan || '-'}</Descriptions.Item>
                <Descriptions.Item label="Số lượng">{ts.soLuong}</Descriptions.Item>
                <Descriptions.Item label="Bộ phận sử dụng">{ts.tenDonVi || form.getFieldValue('tenDonVi') || '-'}</Descriptions.Item>
                <Descriptions.Item label="Nhân viên sử dụng">{ts.nguoiSuDung || '-'}</Descriptions.Item>
                <Descriptions.Item label="Vị trí sử dụng">{ts.viTriSuDung || '-'}</Descriptions.Item>
                <Descriptions.Item label="Trạng thái">{getTrangThaiTag(ts.trangThai)}</Descriptions.Item>
                <Descriptions.Item label="Ngày tăng (Kế toán)">
                    {ts.ngayTang ? (typeof ts.ngayTang === 'string' ? ts.ngayTang : dateCommon.formatDate(ts.ngayTang)) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Ngày đưa vào sử dụng">
                    {ts.ngayDuaVaoSuDung ? (typeof ts.ngayDuaVaoSuDung === 'string' ? ts.ngayDuaVaoSuDung : dateCommon.formatDate(ts.ngayDuaVaoSuDung)) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Ngày bố trí">
                    {ts.ngayNhapKho ? (typeof ts.ngayNhapKho === 'string' ? ts.ngayNhapKho : dateCommon.formatDate(ts.ngayNhapKho)) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Số kỳ phân bổ">
                    {ts.soKyPhanBo ? `${ts.soKyPhanBo} tháng` : (ts.soNamPhanBo ? `${ts.soNamPhanBo} năm` : '-')}
                </Descriptions.Item>
                <Descriptions.Item label="Thời gian bảo hành">
                    {ts.thoiGianBaoHanh ? `${ts.thoiGianBaoHanh} tháng` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Số Serial/Model">{ts.soSerial || '-'}</Descriptions.Item>
                <Descriptions.Item label="Nhà cung cấp">{ts.nhaCungCap || '-'}</Descriptions.Item>
                <Descriptions.Item label="Nguyên giá">
                    {(ts.nguyenGia ?? ts.giaTriKhiNhap) ? formatVND(ts.nguyenGia ?? ts.giaTriKhiNhap) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Đã phân bổ / Hao mòn">
                    {ts.giaTriDaPhanBo ? formatVND(ts.giaTriDaPhanBo) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Giá trị còn lại">
                    {ts.giaTriConLai ? formatVND(ts.giaTriConLai) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Mã QR">
                    {ts.maQR ? (
                        <div>
                            <QRCodeSVG value={ts.maQR} size={100} level="M"
                                style={{ display: 'block', marginBottom: '4px' }} />
                            <span style={{ fontSize: '11px' }}>{ts.maQR}</span>
                        </div>
                    ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Mô tả" span={2}>{ts.moTa || '-'}</Descriptions.Item>
            </Descriptions>
        );
    };

    const renderFormTaiSanPhu = (formInstance: any) => {
        return (
            <Form form={formInstance} layout="vertical" validateMessages={validateMessages}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="tenTaiSan" label="Tên tài sản"
                            rules={[{ required: true }, { max: 255 }]}>
                            <Input placeholder="Tên tài sản phụ" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="ma" label="Mã tài sản (HRM)"
                            rules={[{ required: true, message: 'Mã tài sản là bắt buộc' }, { max: 50 }]}>
                            <Input placeholder="VD: CASE, MON, KB..." />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="maKeToan" label="Mã kế toán (FAST)"
                            rules={[{ max: 50 }]}>
                            <Input placeholder="Mã kế toán (FAST)" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="idLoaiTaiSan" label="Loại tài sản" rules={[{ required: true }]}>
                            <Select notFoundContent="Không có dữ liệu" options={optionLoaiTaiSan}
                                placeholder="Loại tài sản" allowClear />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="idCoSo" label="Cơ sở">
                            <Select allowClear notFoundContent="Không có dữ liệu" options={optionCoSo}
                                placeholder="Chọn cơ sở" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="idBoPhan" label="Vị trí">
                            <Select allowClear showSearch filterOption={(input, opt) => (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                notFoundContent="Không có dữ liệu" options={optionBoPhan}
                                placeholder="Chọn vị trí" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="trangThai" label="Trạng thái" rules={[{ required: true }]}>
                            <Select options={trangThaiOptions} placeholder="Trạng thái" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="idNVSuDung" label="Nhân viên sử dụng">
                            <NhanVienSelectBox allowClear />
                        </Form.Item>
                    </Col>
                    <Col span={18}>
                        <Form.Item name="viTriSuDung" label="Vị trí sử dụng chi tiết">
                            <Input placeholder="Vị trí sử dụng chi tiết" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="ngayTang" label="Ngày tăng (Kế toán)">
                            <DatePicker changeOnBlur style={{ width: "100%" }} placeholder="Ngày tăng"
                                format={["DD/MM/YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="ngayDuaVaoSuDung" label="Ngày đưa vào sử dụng">
                            <DatePicker changeOnBlur style={{ width: "100%" }} placeholder="Ngày đưa vào sử dụng"
                                format={["DD/MM/YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="soKyPhanBo" label="Số kỳ phân bổ (tháng)">
                            <InputNumber placeholder="Số kỳ phân bổ" min={0} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="soLuong" label="Số lượng" rules={[{ required: true }]}>
                            <InputNumber style={{ width: '100%' }} min={1} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="nguyenGia" label="Nguyên giá (VNĐ)">
                            <InputNumber style={{ width: '100%' }} placeholder="Nguyên giá"
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value: any) => value!.replace(/\$\s?|(,*)/g, '')}
                                min={0} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="giaTriDaPhanBo" label="Đã phân bổ / Hao mòn (VNĐ)">
                            <InputNumber style={{ width: '100%' }} placeholder="Giá trị đã phân bổ"
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value: any) => value!.replace(/\$\s?|(,*)/g, '')}
                                min={0} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="giaTriConLai" label="Giá trị còn lại (VNĐ)">
                            <InputNumber style={{ width: '100%' }} placeholder="Giá trị còn lại"
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value: any) => value!.replace(/\$\s?|(,*)/g, '')}
                                min={0} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="soSerial" label="Số Serial/Model">
                            <Input placeholder="Số Serial/Model" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="nhaCungCap" label="Nhà cung cấp">
                            <Input placeholder="Nhà cung cấp" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="thoiGianBaoHanh" label="Thời gian bảo hành (tháng)">
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="moTa" label="Mô tả / Ghi chú">
                            <Input placeholder="Mô tả / Ghi chú tài sản" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        );
    };

    const onFinish = async (values: any) => {
        setIsLoading(true);
        const ngayTang = values.ngayTang ? dateCommon.formatDate(values.ngayTang?.$d) : "";
        const ngayDuaVaoSuDung = values.ngayDuaVaoSuDung ? dateCommon.formatDate(values.ngayDuaVaoSuDung?.$d) : "";
        const ngayNhapKho = values.ngayNhapKho ? dateCommon.formatDate(values.ngayNhapKho?.$d) : "";
        const idDonVi = values.idDonVi;
        const request: TaiSanRequest = {
            files: fileList,
            id: modelProps?.id || undefined,
            ma: values.ma,
            maKeToan: values.maKeToan,
            tenTaiSan: values.tenTaiSan,
            idLoaiTaiSan: values.idLoaiTaiSan,
            idCoSo: values.idCoSo,
            idBoPhan: values.idBoPhan,
            idDonVi,
            idNVSuDung: values.idNVSuDung,
            viTriSuDung: values.viTriSuDung,
            ngayTang,
            ngayDuaVaoSuDung,
            ngayNhapKho,
            soNamPhanBo: values.soNamPhanBo,
            soKyPhanBo: values.soKyPhanBo,
            soKyDaPhanBo: values.soKyDaPhanBo,
            giaTriKhiNhap: values.nguyenGia ?? values.giaTriKhiNhap,
            nguyenGia: values.nguyenGia,
            giaTriDaPhanBo: values.giaTriDaPhanBo,
            giaTriConLai: (values.nguyenGia ?? 0) - (values.giaTriDaPhanBo ?? 0),
            trangThai: values.trangThai,
            soSerial: values.soSerial,
            nhaCungCap: values.nhaCungCap,
            soLuong: values.soLuong,
            thoiGianBaoHanh: values.thoiGianBaoHanh,
            moTa: values.moTa,
            idsTaiLieuTaiSanXoa: idsTaiLieuTaiSanXoa,
        };
        try {
            const response: any = await luuTaiSan(request);
            if (response.trangThai !== responseStatus.thanhCong) {
                toast.error(response.thongBao);
                return;
            }
            const idTaiSanChinh: number = response.duLieu ?? modelProps?.id;
            for (const phu of dsTaiSanPhuPending) {
                const reqPhu: TaiSanRequest = {
                    files: [],
                    ma: phu.ma,
                    maKeToan: phu.maKeToan,
                    tenTaiSan: phu.tenTaiSan,
                    idLoaiTaiSan: phu.idLoaiTaiSan ?? values.idLoaiTaiSan,
                    idCoSo: phu.idCoSo ?? values.idCoSo,
                    idBoPhan: phu.idBoPhan ?? values.idBoPhan,
                    idDonVi,
                    idNVSuDung: phu.idNVSuDung ?? values.idNVSuDung,
                    viTriSuDung: phu.viTriSuDung,
                    ngayTang: phu.ngayTang,
                    ngayNhapKho: phu.ngayNhapKho,
                    ngayDuaVaoSuDung: phu.ngayDuaVaoSuDung,
                    soKyPhanBo: phu.soKyPhanBo,
                    soNamPhanBo: phu.soNamPhanBo,
                    giaTriKhiNhap: phu.nguyenGia ?? phu.giaTriKhiNhap,
                    nguyenGia: phu.nguyenGia ?? phu.giaTriKhiNhap,
                    giaTriDaPhanBo: phu.giaTriDaPhanBo,
                    giaTriConLai: phu.giaTriConLai,
                    trangThai: phu.trangThai,
                    soSerial: phu.soSerial,
                    nhaCungCap: phu.nhaCungCap,
                    soLuong: phu.soLuong,
                    thoiGianBaoHanh: phu.thoiGianBaoHanh,
                    moTa: phu.moTa,
                    idTaiSanChinh,
                };
                await luuTaiSan(reqPhu);
            }
            for (const idXoa of idsXoaTaiSanPhu) {
                await xoaTaiSanPhu(idXoa);
            }
            toast.success('Lưu tài sản thành công');
            onSubmit();
        } catch (error: any) {
            toast.error('Lỗi khi lưu tài sản: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadDataSelectBoxs();
        setFormDetailModal(modelProps);
    }, [modelProps]);

    const columnsTaiSanPhuHienTai = [
        {
            title: 'Tên tài sản',
            dataIndex: 'tenTaiSan',
            key: 'tenTaiSan',
            render: (ten: string, record: TaiSan) => (
                <div>
                    <span style={{ fontWeight: 500 }}>{ten}</span>
                    <div style={{ fontSize: '11px', color: '#888' }}>
                        HRM: {record.ma} {record.maKeToan ? ` | FAST: ${record.maKeToan}` : ''}
                    </div>
                </div>
            )
        },
        { title: 'Bộ phận sử dụng', dataIndex: 'tenDonVi', key: 'tenDonVi' },
        { title: 'Vị trí sử dụng', dataIndex: 'viTriSuDung', key: 'viTriSuDung' },
        { title: 'Loại', dataIndex: 'loaiTaiSan', key: 'loaiTaiSan', width: 130 },
        { title: 'SL', dataIndex: 'soLuong', key: 'soLuong', width: 60, align: 'center' as const },
        {
            title: 'Nguyên giá',
            key: 'nguyenGia',
            width: 120,
            align: 'right' as const,
            render: (_: any, record: TaiSan) => ((record.nguyenGia ?? record.giaTriKhiNhap) ? formatVND(record.nguyenGia ?? record.giaTriKhiNhap) : '-')
        },
        {
            title: 'Trạng thái', dataIndex: 'trangThai', key: 'trangThai', width: 120,
            render: (v: number) => getTrangThaiTag(v)
        },
        {
            title: '', key: 'action', width: 120, align: 'center' as const,
            render: (_: any, record: TaiSan) => (
                <Space>
                    <Button type="link" size="small" icon={<EyeOutlined />}
                        onClick={() => handleXemPhuHienTai(record)} />
                    <Button type="link" size="small" onClick={() => handleMoModalSuaPhuHienTai(record)}>Sửa</Button>
                    <Button type="link" danger size="small" icon={<DeleteOutlined />}
                        onClick={() => handleXoaTaiSanPhuHienTai(record.id!)} />
                </Space>
            )
        },
    ];

    const columnsTaiSanPhuPending = [
        {
            title: 'Tên tài sản',
            dataIndex: 'tenTaiSan',
            key: 'tenTaiSan',
            render: (ten: string, record: TaiSanPhuPending) => (
                <div>
                    <span style={{ fontWeight: 500 }}>{ten}</span>
                    <div style={{ fontSize: '11px', color: '#888' }}>
                        HRM: {record.ma} {record.maKeToan ? ` | FAST: ${record.maKeToan}` : ''}
                    </div>
                </div>
            )
        },
        { title: 'Bộ phận sử dụng', dataIndex: 'tenDonVi', key: 'tenDonVi' },
        { title: 'Vị trí sử dụng', dataIndex: 'viTriSuDung', key: 'viTriSuDung' },
        { title: 'Loại', dataIndex: 'tenLoaiTaiSan', key: 'tenLoaiTaiSan', width: 130 },
        { title: 'SL', dataIndex: 'soLuong', key: 'soLuong', width: 60, align: 'center' as const },
        {
            title: 'Nguyên giá',
            key: 'nguyenGia',
            width: 120,
            align: 'right' as const,
            render: (_: any, record: TaiSanPhuPending) => ((record.nguyenGia ?? record.giaTriKhiNhap) ? formatVND(record.nguyenGia ?? record.giaTriKhiNhap) : '-')
        },
        {
            title: 'Trạng thái', dataIndex: 'trangThai', key: 'trangThai', width: 120,
            render: (v: number) => getTrangThaiTag(v)
        },
        {
            title: '', key: 'action', width: 100, align: 'center' as const,
            render: (_: any, record: TaiSanPhuPending) => (
                <Space>
                    <Button type="link" size="small" onClick={() => handleMoModalTaiSanPhu(record)}>Sửa</Button>
                    <Button type="link" danger size="small" icon={<DeleteOutlined />}
                        onClick={() => handleXoaTaiSanPhuPending(record.tempId)} />
                </Space>
            )
        },
    ];

    return (
        <>
            <Form layout="vertical" onFinish={onFinish} validateMessages={validateMessages} form={form}>
                <Row gutter={16}>
                    <Col className="gutter-row" span="12">
                        <Form.Item name="tenTaiSan" label="Tên tài sản"
                            rules={[{ required: true }, { max: 255 }]}>
                            <Input placeholder="Tên tài sản" />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="ma" label="Mã tài sản (HRM)"
                            rules={[{ required: false }, { max: 50 }]}>
                            <Input placeholder="Mã tài sản (HRM)" />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="maKeToan" label="Mã kế toán (FAST)"
                            rules={[{ required: false }, { max: 50 }]}>
                            <Input placeholder="Mã kế toán (FAST)" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="idLoaiTaiSan" label="Loại tài sản" rules={[{ required: true }]}>
                            <Select notFoundContent="Không có dữ liệu" options={optionLoaiTaiSan}
                                loading={isLoading} placeholder="Loại tài sản" />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="idCoSo" label="Cơ sở">
                            <Select allowClear notFoundContent="Không có dữ liệu" options={optionCoSo}
                                loading={isLoading} placeholder="Chọn cơ sở" />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="idBoPhan" label="Vị trí">
                            <Select allowClear showSearch filterOption={(input, opt) => (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                notFoundContent="Không có dữ liệu" options={optionBoPhan}
                                loading={isLoading} placeholder="Chọn vị trí" />
                        </Form.Item>
                    </Col>
                    <Col span="6">
                        <Form.Item name="trangThai" label="Trạng thái" rules={[{ required: true }]}>
                            <Select options={trangThaiOptions} placeholder="Trạng thái" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="idDonVi" label="Bộ phận sử dụng (Phòng ban)" rules={[{ required: true }]}>
                            <DonViSelectTree allowClear={false} autoSelectFirst={!modelProps?.idDonVi} />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="idNVSuDung" label="Nhân viên sử dụng">
                            <NhanVienSelectBox allowClear />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="12">
                        <Form.Item name="viTriSuDung" label="Vị trí sử dụng chi tiết">
                            <Input placeholder="Vị trí sử dụng chi tiết" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span="6">
                        <Form.Item name="ngayTang" label="Ngày tăng (Kế toán)">
                            <DatePicker changeOnBlur style={{ width: "100%" }} placeholder="Ngày tăng"
                                format={["DD/MM/YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                    </Col>
                    <Col span="6">
                        <Form.Item name="ngayDuaVaoSuDung" label="Ngày đưa vào sử dụng">
                            <DatePicker changeOnBlur style={{ width: "100%" }} placeholder="Ngày đưa vào sử dụng"
                                format={["DD/MM/YYYY", "DDMMYYYY"]} />
                        </Form.Item>
                    </Col>
                    <Col span="6">
                        <Form.Item name="soKyPhanBo" label="Số kỳ phân bổ (tháng)">
                            <InputNumber placeholder="Số kỳ phân bổ" min={0} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="soLuong" label="Số lượng" rules={[{ required: true }]}>
                            <InputNumber style={{ width: '100%' }} placeholder="Số lượng" min={1} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="nguyenGia" label="Nguyên giá (VNĐ)">
                            <InputNumber style={{ width: '100%' }} placeholder="Nguyên giá"
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value: any) => value!.replace(/\$\s?|(,*)/g, '')}
                                min={0} />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="giaTriDaPhanBo" label="Đã phân bổ / Hao mòn (VNĐ)">
                            <InputNumber style={{ width: '100%' }} placeholder="Giá trị đã phân bổ"
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value: any) => value!.replace(/\$\s?|(,*)/g, '')}
                                min={0} />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="soSerial" label="Số Serial/Model">
                            <Input placeholder="Số Serial/Model" />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="nhaCungCap" label="Nhà cung cấp">
                            <Input placeholder="Nhà cung cấp" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col className="gutter-row" span="6">
                        <Form.Item name="thoiGianBaoHanh" label="Thời gian bảo hành (tháng)">
                            <InputNumber style={{ width: '100%' }} placeholder="Thời gian bảo hành" min={0} />
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span="18">
                        <Form.Item name="moTa" label="Mô tả / Ghi chú">
                            <Input placeholder="Mô tả / Ghi chú tài sản" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span="24">
                        <Form.Item label="Tài liệu đính kèm">
                            <Upload {...uploadProps}>
                                <Button><UploadOutlined /> Thêm file</Button>
                            </Upload>
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <strong>Danh sách tài sản phụ</strong>
                                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleMoModalTaiSanPhu()} size="small">
                                    Thêm tài sản phụ
                                </Button>
                            </div>
                            {dsTaiSanPhuHienTai.length > 0 && (
                                <Table columns={columnsTaiSanPhuHienTai} dataSource={dsTaiSanPhuHienTai}
                                    rowKey="id" pagination={false} size="small"
                                    style={{ marginBottom: dsTaiSanPhuPending.length > 0 ? 8 : 0 }} />
                            )}
                            {dsTaiSanPhuPending.length > 0 && (
                                <Table columns={columnsTaiSanPhuPending} dataSource={dsTaiSanPhuPending}
                                    rowKey="tempId" pagination={false} size="small"
                                    title={() => <span style={{ color: '#1677ff', fontSize: 12 }}>Tài sản phụ mới (chưa lưu)</span>} />
                            )}
                            {dsTaiSanPhuHienTai.length === 0 && dsTaiSanPhuPending.length === 0 && (
                                <div style={{ textAlign: 'center', color: '#999', padding: '12px 0' }}>
                                    Chưa có tài sản phụ
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col span={24} className="text-right">
                        <Link to={Links.TaiSan.DanhSach}>
                            <Button className="mr-2">Quay lại</Button>
                        </Link>
                        <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={isLoading}>
                            Lưu
                        </Button>
                    </Col>
                </Row>
            </Form>

            <Modal
                title={editingTaiSanPhu ? 'Sửa tài sản phụ' : 'Thêm tài sản phụ'}
                open={showModalTaiSanPhu}
                onOk={handleLuuTaiSanPhu}
                onCancel={() => setShowModalTaiSanPhu(false)}
                okText="Xác nhận"
                cancelText="Hủy"
                width={900}
                destroyOnClose
            >
                {renderFormTaiSanPhu(formTaiSanPhu)}
            </Modal>

            {/* Hidden print template tài sản phụ (dùng cho In QR từ modal Xem) */}
            <div style={{ display: 'none' }}>
                <div ref={componentRefPhuXem} style={{ padding: '20px' }}>
                    {taiSanPhuDangXem && (
                        <div style={{ textAlign: 'center', border: '1px solid #ddd', padding: '20px', maxWidth: '400px', margin: '0 auto' }}>
                            <h3 style={{ marginBottom: '15px' }}>{taiSanPhuDangXem.tenTaiSan}</h3>
                            <QRCodeSVG
                                value={taiSanPhuDangXem.maQR || ''}
                                size={300}
                                level="M"
                                style={{ maxWidth: '100%' }}
                            />
                            <div style={{ marginTop: '15px', fontSize: '14px' }}>
                                <p><strong>Mã tài sản:</strong> {taiSanPhuDangXem.ma}</p>
                                <p><strong>Bộ phận sử dụng:</strong> {taiSanPhuDangXem.tenDonVi}</p>
                                {taiSanPhuDangXem.viTriSuDung && <p><strong>Vị trí:</strong> {taiSanPhuDangXem.viTriSuDung}</p>}
                                <p><strong>Mã QR:</strong> {taiSanPhuDangXem.maQR}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal xem chi tiết tài sản phụ hiện tại */}
            <Modal
                title="Chi tiết tài sản phụ"
                open={showModalXemPhuHienTai}
                onCancel={() => setShowModalXemPhuHienTai(false)}
                footer={[
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintPhuXem}>
                        In QR
                    </Button>,
                    <Button key="close" onClick={() => setShowModalXemPhuHienTai(false)}>Đóng</Button>,
                ]}
                width={900}
            >
                {taiSanPhuDangXem && renderChiTietTaiSanPhu(taiSanPhuDangXem)}
            </Modal>

            {/* Modal sửa tài sản phụ hiện tại (đã có trong DB) */}
            <Modal
                title="Sửa tài sản phụ"
                open={showModalSuaPhuHienTai}
                onOk={handleLuuSuaPhuHienTai}
                onCancel={() => setShowModalSuaPhuHienTai(false)}
                okText="Lưu"
                cancelText="Hủy"
                width={900}
                destroyOnClose
            >
                {renderFormTaiSanPhu(formSuaPhuHienTai)}
            </Modal>
        </>
    );
};

export default LuuTaiSanBox;
